import { useEffect, useRef, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import api from '@/utils/api';
import { toast } from 'sonner';
import { Loader2, Save, Trash2, Type, FileUp, RefreshCw } from 'lucide-react';
import { PdfPositionEditor } from '@/components/teacher/PdfPositionEditor';

const STATUS_STYLES = {
  draft: { bg: '#F0EFEA', color: '#5C635A', label: 'Draft - mark the positions and submit' },
  pending: { bg: '#FEF3C7', color: '#B45309', label: 'Pending admin approval' },
  approved: { bg: '#E8F5E9', color: '#2E7D32', label: 'Approved - available to learners' },
  rejected: { bg: '#FEE2E2', color: '#DC2626', label: 'Rejected - please revise and resubmit' },
};

const PLACEHOLDERS = ['[name and surname]', '[Reference]', '[Teacher]', '[Date]'];

const ModeToggle = ({ mode, setMode }) => (
  <div className="inline-flex rounded-full p-1" style={{ backgroundColor: '#F0EFEA' }} role="tablist">
    {[['text', 'Type the letter', Type], ['pdf', 'Upload my own PDF', FileUp]].map(([key, label, Icon]) => (
      <button
        key={key}
        type="button"
        role="tab"
        aria-selected={mode === key}
        onClick={() => setMode(key)}
        className="flex items-center gap-1.5 text-xs sm:text-sm px-3 sm:px-4 py-1.5 rounded-full transition-colors"
        style={mode === key ? { backgroundColor: '#163329', color: '#FFFFFF' } : { color: '#5C635A' }}
        data-testid={`model-letter-mode-${key}`}
      >
        <Icon className="h-4 w-4" /> {label}
      </button>
    ))}
  </div>
);

export const ModelLetterEditor = ({ courseId }) => {
  const [letter, setLetter] = useState(null);
  const [mode, setMode] = useState('text');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const applyLetter = (l) => {
    setLetter(l);
    setText(l?.text || '');
    setMode(l?.mode === 'pdf' ? 'pdf' : 'text');
  };

  const load = async () => {
    try {
      const res = await api.get(`/courses/${courseId}/model-letter`);
      applyLetter(res.data.has_letter ? res.data.letter : null);
    } catch (e) {
      console.error('Failed to load model letter', e);
    }
  };

  useEffect(() => { load(); }, [courseId]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveText = async () => {
    if (!text.trim()) return toast.error('Letter text cannot be empty');
    setBusy(true);
    try {
      if (letter) await api.put(`/courses/${courseId}/model-letter`, { text });
      else await api.post(`/courses/${courseId}/model-letter`, { text });
      toast.success('Letter submitted for admin approval');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save letter');
    } finally {
      setBusy(false);
    }
  };

  const uploadPdf = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.type !== 'application/pdf') return toast.error('Please choose a PDF file');
    if (file.size > 10 * 1024 * 1024) return toast.error('PDF must be under 10MB');
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await api.post(`/courses/${courseId}/model-letter/template`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      applyLetter(res.data.letter);
      toast.success('PDF uploaded. Now mark where each detail goes.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload PDF');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/courses/${courseId}/model-letter`);
      toast.success('Letter deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete letter');
    } finally {
      setBusy(false);
    }
  };

  const status = letter && STATUS_STYLES[letter.status];
  const hasPdf = letter?.mode === 'pdf' && letter.template_file_key;

  return (
    <div className="pt-6 border-t space-y-4" style={{ borderColor: '#E3E1DB' }} data-testid="model-letter-section">
      <div>
        <h2 className="text-2xl font-semibold" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>Letter of Model Acquisition</h2>
        <p className="text-sm mt-1" style={{ color: '#5C635A' }}>
          Enrolled learners can download this letter as a PDF signed by you (the course teacher). Requires admin approval.
        </p>
      </div>
      <Card className="p-4 sm:p-6 space-y-4" style={{ borderColor: '#E3E1DB' }}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <ModeToggle mode={mode} setMode={setMode} />
          {status && (
            <span className="inline-block text-xs px-3 py-1 rounded-full" style={{ backgroundColor: status.bg, color: status.color }} data-testid="model-letter-status">
              {status.label}{letter.mode === 'pdf' ? ' (PDF letter)' : ' (typed letter)'}
            </span>
          )}
        </div>
        {letter && mode !== (letter.mode || 'text') && (
          <p className="text-xs italic" style={{ color: '#B45309' }}>
            Saving in this mode replaces your current {letter.mode === 'pdf' ? 'PDF' : 'typed'} letter and sends it for approval again.
          </p>
        )}

        {mode === 'text' ? (
          <>
            <div className="flex flex-wrap gap-2 text-xs" style={{ color: '#5C635A' }}>
              Placeholders:
              {PLACEHOLDERS.map((p) => (
                <button key={p} type="button" className="px-2 py-0.5 rounded border hover:bg-[#F0EFEA]" style={{ borderColor: '#E3E1DB' }} onClick={() => setText((t) => t + p)} data-testid={`insert-placeholder-${p.replace(/[^a-z]/gi, '').toLowerCase()}`}>
                  {p}
                </button>
              ))}
            </div>
            <Textarea rows={10} value={text} onChange={(e) => setText(e.target.value)} placeholder="This letter confirms that [name and surname] (ID: [Reference]) acquired the model taught by [Teacher] on [Date]..." data-testid="model-letter-textarea" />
            <Button onClick={saveText} disabled={busy} style={{ backgroundColor: '#163329' }} data-testid="save-model-letter-btn">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              {letter && letter.mode !== 'pdf' ? 'Update & Resubmit' : 'Submit for Approval'}
            </Button>
          </>
        ) : (
          <>
            <input ref={fileRef} type="file" accept="application/pdf,.pdf" className="hidden" onChange={uploadPdf} data-testid="model-letter-pdf-input" />
            <div className="flex flex-wrap items-center gap-3">
              <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={busy} style={{ borderColor: '#163329', color: '#163329' }} data-testid="upload-model-letter-pdf-btn">
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : hasPdf ? <RefreshCw className="h-4 w-4 mr-2" /> : <FileUp className="h-4 w-4 mr-2" />}
                {hasPdf ? 'Replace PDF' : 'Upload PDF letter'}
              </Button>
              {hasPdf && <span className="text-xs truncate max-w-[220px]" style={{ color: '#5C635A' }}>{letter.template_filename}</span>}
              {!hasPdf && <span className="text-xs" style={{ color: '#8B9187' }}>PDF up to 10MB</span>}
            </div>
            {hasPdf && <PdfPositionEditor courseId={courseId} letter={letter} onSaved={applyLetter} />}
          </>
        )}

        {letter && (
          <Button variant="outline" onClick={remove} disabled={busy} className="text-red-600 border-red-200" data-testid="delete-model-letter-btn">
            <Trash2 className="h-4 w-4 mr-2" /> Delete Letter
          </Button>
        )}
      </Card>
    </div>
  );
};
